import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowDown,
  FaArrowRight,
  FaArrowUp,
  FaBox,
  FaChevronDown,
  FaChevronRight,
  FaEye,
  FaGripVertical,
  FaSave,
  FaSearch,
  FaSitemap,
  FaTimes,
} from "react-icons/fa";
import CommonService from "../../services/common.service";
import b2bHubHomeService from "../../services/b2bHubHome.service";
import {
  fetchHubHomeSections,
  hubHomeSectionExtractMessage,
  updateHubHomeSection,
} from "../../services/hubHomeSectionAdmin.service";
import {
  buildTradingCategoriesSectionConfig,
  HUB_HOME_SECTION_KEYS,
  resolveSectionGridConfig,
  resolveTradingCategorySelections,
  TRADING_CATEGORY_MAX_SUBCATEGORIES,
} from "../../constants/hubHomeSections";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import "../../assets/styles/master_category_admin.scss";
import "../../assets/styles/super_admin.scss";
import "../../assets/styles/super_admin_service_quick_links.scss";
import "../../assets/styles/super_admin_trading_categories.scss";

const mapCategory = (row) => ({
  id: Number(row.category_id ?? row.id),
  name: String(row.category ?? row.category_name ?? row.name ?? "").trim(),
  image_path: row.image_path ?? null,
  product_count: Number(row.product_count ?? 0),
  supplier_count: Number(row.supplier_count ?? 0),
  buyer_request_count: Number(row.buyer_request_count ?? row.rfq_count ?? 0),
});

const mapSubcategory = (row) => ({
  id: Number(row.sub_category_id ?? row.id),
  name: String(row.sub_category ?? row.name ?? "").trim(),
  product_count: Number(row.product_count ?? 0),
  supplier_count: Number(row.supplier_count ?? 0),
  buyer_request_count: Number(row.buyer_request_count ?? row.rfq_count ?? 0),
});

const formatStatCount = (value) => (Number(value) || 0).toLocaleString();

const categoryActivityScore = (category) =>
  Number(category?.product_count ?? 0) +
  Number(category?.supplier_count ?? 0) +
  Number(category?.buyer_request_count ?? 0);

const compareCategoriesByActivity = (a, b) => {
  const scoreDiff = categoryActivityScore(b) - categoryActivityScore(a);
  if (scoreDiff !== 0) return scoreDiff;

  const productDiff = Number(b.product_count ?? 0) - Number(a.product_count ?? 0);
  if (productDiff !== 0) return productDiff;

  const supplierDiff = Number(b.supplier_count ?? 0) - Number(a.supplier_count ?? 0);
  if (supplierDiff !== 0) return supplierDiff;

  const rfqDiff = Number(b.buyer_request_count ?? 0) - Number(a.buyer_request_count ?? 0);
  if (rfqDiff !== 0) return rfqDiff;

  return a.name.localeCompare(b.name);
};

const sortCategoriesByActivity = (categories) => [...categories].sort(compareCategoriesByActivity);

const TRADING_CATEGORIES_PAGE_SIZE = 10;

function extractCategoryRows(response) {
  const envelope = response?.data ?? response ?? {};
  if (Array.isArray(envelope.categories)) return envelope.categories;
  if (Array.isArray(envelope?.data?.categories)) return envelope.data.categories;
  if (Array.isArray(envelope)) return envelope;
  if (Array.isArray(envelope.data)) return envelope.data;
  return [];
}

function extractListRows(response) {
  const payload = response?.data ?? response ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function CategoryStats({ item, compact = false }) {
  return (
    <div className={`sa-tc-stats${compact ? " sa-tc-stats--compact" : ""}`}>
      <span className="sa-tc-stats__item">
        <strong>{formatStatCount(item.product_count)}</strong> Products
      </span>
      <span className="sa-tc-stats__item">
        <strong>{formatStatCount(item.supplier_count)}</strong> Suppliers
      </span>
      <span className="sa-tc-stats__item">
        <strong>{formatStatCount(item.buyer_request_count)}</strong> RFQs
      </span>
    </div>
  );
}

function ToggleSwitch({ checked, disabled, onChange, label }) {
  return (
    <label className="sa-toggle" title={label}>
      <input
        type="checkbox"
        className="sa-toggle__input"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span className="sa-toggle__track" aria-hidden />
    </label>
  );
}

function SubcategoriesModal({
  open,
  category,
  saving,
  selectedSubIds,
  subcategories,
  loading,
  onClose,
  onToggleSubcategory,
  onMoveSubcategory,
}) {
  if (!open || !category) return null;

  return (
    <div className="sa-modal-backdrop" role="presentation" onClick={() => !saving && onClose()}>
      <div
        className="sa-modal sa-modal--trading-sub"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sa-tc-sub-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sa-tc-modal__head">
          <div>
            <h2 id="sa-tc-sub-modal-title" className="mca-section-title">
              Sub-categories for {category.name}
            </h2>
            <p className="sa-modal__lead">
              Select up to {TRADING_CATEGORY_MAX_SUBCATEGORIES} sub-categories to show on the home card (sorted by
              activity).
            </p>
          </div>
          <button
            type="button"
            className="sa-tc-modal__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close sub-categories popup"
          >
            <FaTimes aria-hidden />
          </button>
        </div>

        {loading ? (
          <p className="sa-tc-sub-panel__loading">Loading sub-categories…</p>
        ) : subcategories.length ? (
          <ul className="sa-tc-sub-list sa-tc-sub-list--modal">
            {subcategories.map((subcategory) => {
              const isSubOn = selectedSubIds.includes(subcategory.id);
              const subIndex = selectedSubIds.indexOf(subcategory.id);
              const canMoveSub = isSubOn && selectedSubIds.length > 1;

              return (
                <li
                  key={subcategory.id}
                  className={`sa-tc-sub-list__item${isSubOn ? "" : " sa-tc-sub-list__item--off"}`}
                >
                  <div className="sa-tc-sub-list__row">
                    <span className={`sa-tc-sub-list__order${isSubOn ? "" : " sa-tc-sub-list__order--off"}`}>
                      {isSubOn ? subIndex + 1 : "—"}
                    </span>
                    <div className="sa-tc-sub-list__copy">
                      <span className="sa-tc-sub-list__name">{subcategory.name}</span>
                      <CategoryStats item={subcategory} compact />
                    </div>
                    <div className="sa-tc-sub-list__controls">
                      <ToggleSwitch
                        checked={isSubOn}
                        disabled={
                          saving ||
                          (!isSubOn && selectedSubIds.length >= TRADING_CATEGORY_MAX_SUBCATEGORIES)
                        }
                        onChange={(checked) => onToggleSubcategory(subcategory.id, checked)}
                        label={`${isSubOn ? "Hide" : "Show"} ${subcategory.name}`}
                      />
                      {isSubOn ? (
                        <div className="sa-sql-tile-list__order-btns">
                          <button
                            type="button"
                            className="sa-sql-order-btn"
                            disabled={!canMoveSub || saving || subIndex === 0}
                            onClick={() => onMoveSubcategory(subcategory.id, "up")}
                            aria-label={`Move ${subcategory.name} up`}
                          >
                            <FaArrowUp aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="sa-sql-order-btn"
                            disabled={!canMoveSub || saving || subIndex === selectedSubIds.length - 1}
                            onClick={() => onMoveSubcategory(subcategory.id, "down")}
                            aria-label={`Move ${subcategory.name} down`}
                          >
                            <FaArrowDown aria-hidden />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="sa-tc-sub-panel__empty">No sub-categories found for this category.</p>
        )}

        <div className="sa-modal__actions">
          <button type="button" className="mca-btn mca-btn--primary" onClick={onClose} disabled={saving}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SuperAdminTradingCategoriesPage() {
  const [section, setSection] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [categorySelections, setCategorySelections] = useState([]);
  const [subcategoryModalCategoryId, setSubcategoryModalCategoryId] = useState(null);
  const [subcategoryCache, setSubcategoryCache] = useState({});
  const [loadingSubcategoryId, setLoadingSubcategoryId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const [visibleCategoryCount, setVisibleCategoryCount] = useState(TRADING_CATEGORIES_PAGE_SIZE);

  const categoryById = useMemo(
    () => Object.fromEntries(allCategories.map((category) => [category.id, category])),
    [allCategories],
  );

  const displayConfig = useMemo(() => {
    if (!section) {
      return resolveSectionGridConfig(HUB_HOME_SECTION_KEYS.TRADING_CATEGORIES, null);
    }
    return resolveSectionGridConfig(
      HUB_HOME_SECTION_KEYS.TRADING_CATEGORIES,
      section.sectionConfig ?? section.section_config,
    );
  }, [section]);

  const maxCategories = displayConfig.total_items;

  const visibleCategoryIds = useMemo(
    () => categorySelections.map((selection) => selection.category_id),
    [categorySelections],
  );

  const orderedCategoryOptions = useMemo(() => sortCategoriesByActivity(allCategories), [allCategories]);

  const filteredCategoryOptions = useMemo(() => {
    const query = categorySearchQuery.trim().toLowerCase();
    if (!query) return orderedCategoryOptions;
    return orderedCategoryOptions.filter((category) => category.name.toLowerCase().includes(query));
  }, [orderedCategoryOptions, categorySearchQuery]);

  const visibleCategoryOptions = useMemo(
    () => filteredCategoryOptions.slice(0, visibleCategoryCount),
    [filteredCategoryOptions, visibleCategoryCount],
  );

  const hasMoreCategories = visibleCategoryCount < filteredCategoryOptions.length;
  const nextCategoryBatchSize = Math.min(
    TRADING_CATEGORIES_PAGE_SIZE,
    filteredCategoryOptions.length - visibleCategoryCount,
  );

  const selectedCategoriesByActivity = useMemo(
    () =>
      sortCategoriesByActivity(
        categorySelections.map((selection) => categoryById[selection.category_id]).filter(Boolean),
      ),
    [categorySelections, categoryById],
  );

  const buildActivityOrderedSelections = useCallback(
    (selections) =>
      sortCategoriesByActivity(
        selections.map((selection) => categoryById[selection.category_id]).filter(Boolean),
      ).map((category) => {
        const source = selections.find((selection) => selection.category_id === category.id);
        return {
          category_id: category.id,
          subcategory_ids: source?.subcategory_ids ?? [],
        };
      }),
    [categoryById],
  );

  const isManualMode = visibleCategoryIds.length > 0;
  const atCategoryLimit = visibleCategoryIds.length >= maxCategories;

  const loadSubcategories = useCallback(async (categoryId) => {
    if (!categoryId || subcategoryCache[categoryId]) return subcategoryCache[categoryId];

    setLoadingSubcategoryId(categoryId);
    try {
      const response = await CommonService.get(`/master/master-sub-categories/${categoryId}`, {}, { silent: true });
      const rows = extractListRows(response);
      const mapped = sortCategoriesByActivity(rows.map(mapSubcategory).filter((row) => row.id > 0 && row.name));
      setSubcategoryCache((prev) => ({ ...prev, [categoryId]: mapped }));
      return mapped;
    } catch (err) {
      toast.error("Could not load sub-categories for this category.");
      return [];
    } finally {
      setLoadingSubcategoryId(null);
    }
  }, [subcategoryCache]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sections, categoriesResponse] = await Promise.all([
        fetchHubHomeSections(),
        b2bHubHomeService.getProductCategories(),
      ]);

      const tradingSection = sections.find(
        (row) => (row.sectionKey || row.section_key) === HUB_HOME_SECTION_KEYS.TRADING_CATEGORIES,
      );
      const categoryRows = extractCategoryRows(categoriesResponse);

      const sectionConfig = tradingSection?.sectionConfig ?? tradingSection?.section_config;
      const grid = resolveSectionGridConfig(HUB_HOME_SECTION_KEYS.TRADING_CATEGORIES, sectionConfig);

      setSection(tradingSection || null);
      setAllCategories(
        sortCategoriesByActivity(categoryRows.map(mapCategory).filter((row) => row.id > 0 && row.name)),
      );
      setCategorySelections(
        resolveTradingCategorySelections(sectionConfig, grid.total_items),
      );
      setVisibleCategoryCount(TRADING_CATEGORIES_PAGE_SIZE);
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not load Trending Categories settings."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setVisibleCategoryCount(TRADING_CATEGORIES_PAGE_SIZE);
  }, [categorySearchQuery]);

  useEffect(() => {
    if (subcategoryModalCategoryId) {
      void loadSubcategories(subcategoryModalCategoryId);
    }
  }, [subcategoryModalCategoryId, loadSubcategories]);

  const getSelection = (categoryId) =>
    categorySelections.find((selection) => selection.category_id === categoryId);

  const updateSubcategoryIds = (categoryId, subcategoryIds) => {
    setCategorySelections((prev) =>
      prev.map((selection) =>
        selection.category_id === categoryId
          ? { ...selection, subcategory_ids: subcategoryIds.slice(0, TRADING_CATEGORY_MAX_SUBCATEGORIES) }
          : selection,
      ),
    );
  };

  const toggleCategory = (categoryId, checked) => {
    if (checked) {
      setCategorySelections((prev) => {
        if (prev.some((selection) => selection.category_id === categoryId)) return prev;
        if (prev.length >= maxCategories) {
          toast.warn(
            `You can select up to ${maxCategories} categories (${displayConfig.rows} rows × ${displayConfig.products_per_row} per row). Update this in Home Section Management.`,
          );
          return prev;
        }
        return [...prev, { category_id: categoryId, subcategory_ids: [] }];
      });
      return;
    }
    if (categorySelections.length <= 1) return;
    setCategorySelections((prev) => prev.filter((selection) => selection.category_id !== categoryId));
    if (subcategoryModalCategoryId === categoryId) {
      setSubcategoryModalCategoryId(null);
    }
  };

  const toggleSubcategory = (categoryId, subcategoryId, checked) => {
    const selection = getSelection(categoryId);
    if (!selection) return;

    const current = Array.isArray(selection.subcategory_ids) ? [...selection.subcategory_ids] : [];
    if (checked) {
      if (current.includes(subcategoryId) || current.length >= TRADING_CATEGORY_MAX_SUBCATEGORIES) return;
      updateSubcategoryIds(categoryId, [...current, subcategoryId]);
      return;
    }
    updateSubcategoryIds(
      categoryId,
      current.filter((id) => id !== subcategoryId),
    );
  };

  const moveSubcategory = (categoryId, subcategoryId, direction) => {
    const selection = getSelection(categoryId);
    if (!selection) return;

    const current = Array.isArray(selection.subcategory_ids) ? [...selection.subcategory_ids] : [];
    const index = current.indexOf(subcategoryId);
    if (index < 0) return;
    const nextIndex = direction === "up" ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= current.length) return;
    [current[index], current[nextIndex]] = [current[nextIndex], current[index]];
    updateSubcategoryIds(categoryId, current);
  };

  const closeSubcategoriesModal = () => {
    setSubcategoryModalCategoryId(null);
  };

  const subcategoryModalCategory = subcategoryModalCategoryId
    ? categoryById[subcategoryModalCategoryId] ?? null
    : null;
  const subcategoryModalSelection = subcategoryModalCategoryId
    ? getSelection(subcategoryModalCategoryId)
    : null;

  const saveCategories = async () => {
    if (!section) {
      toast.error("Trending Categories section was not found.");
      return;
    }
    if (!categorySelections.length) {
      toast.error("Select at least one category to display on the home page.");
      return;
    }
    if (categorySelections.length > maxCategories) {
      toast.error(`Select at most ${maxCategories} categories for the current display layout.`);
      return;
    }

    const categoriesMissingSubcategories = categorySelections.filter(
      (selection) => !Array.isArray(selection.subcategory_ids) || !selection.subcategory_ids.length,
    );
    if (categoriesMissingSubcategories.length) {
      const names = categoriesMissingSubcategories
        .map((selection) => categoryById[selection.category_id]?.name)
        .filter(Boolean);
      const label =
        names.length > 0
          ? names.join(", ")
          : `${categoriesMissingSubcategories.length} selected categor${
              categoriesMissingSubcategories.length === 1 ? "y" : "ies"
            }`;
      toast.error(`Select at least one sub-category for: ${label}`);
      return;
    }

    setSaving(true);
    try {
      const orderedSelections = buildActivityOrderedSelections(categorySelections).slice(0, maxCategories);
      const updated = await updateHubHomeSection(section.id, {
        section_config: buildTradingCategoriesSectionConfig(
          section.sectionConfig ?? section.section_config,
          orderedSelections,
        ),
      });
      if (updated) {
        setSection(updated);
        const grid = resolveSectionGridConfig(
          HUB_HOME_SECTION_KEYS.TRADING_CATEGORIES,
          updated.sectionConfig ?? updated.section_config,
        );
        setCategorySelections(
          resolveTradingCategorySelections(updated.sectionConfig ?? updated.section_config, grid.total_items),
        );
        toast.success("Trending Categories updated.");
      }
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not update Trending Categories."));
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mca-page super-admin-page sa-sql-page sa-tc-page">
      <nav className="mca-breadcrumb" aria-label="Breadcrumb">
        <ol className="mca-breadcrumb__list">
          <li>
            <Link to="/super-admin/dashboard">Super Admin</Link>
          </li>
          <li>
            <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>
          </li>
          <li aria-current="page">Trending Categories</li>
        </ol>
      </nav>

      <div className="sa-sql-page-head">
        <header className="sa-sql-hero">
          <div className="sa-sql-hero__title-row">
            <span className="sa-sql-hero__icon" aria-hidden>
              <FaSitemap />
            </span>
            <h1 className="sa-sql-hero__title">Trending Categories</h1>
            <span className="sa-sql-hero__badge">Super Admin</span>
          </div>
          <p className="sa-sql-hero__subtitle">
            Choose which marketplace categories appear in the Trending Categories section on the home page and pick up to
            four sub-categories per card. Categories are sorted by highest trading activity (products + suppliers +
            RFQs). You can select up to <strong>{maxCategories}</strong> categories ({displayConfig.rows} rows ×{" "}
            {displayConfig.products_per_row} per row) from{" "}
            <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>.
          </p>
        </header>
        <div className="sa-sql-page-head__actions">
          <Link
            to="/home"
            className="mca-btn mca-btn--outline sa-sql-preview-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaEye aria-hidden /> Preview on Home Page
          </Link>
          <button
            type="button"
            className="mca-btn mca-btn--primary sa-sql-save-btn"
            onClick={() => void saveCategories()}
            disabled={loading || saving || !section}
          >
            <FaSave aria-hidden /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="sa-placeholder-copy">Loading categories…</p>
      ) : !section ? (
        <p className="sa-placeholder-copy">
          Trending Categories section is missing. Restore it from{" "}
          <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>.
        </p>
      ) : (
        <>
          {!isManualMode ? (
            <div className="sa-tc-notice">
              No manual selection saved yet. The home page currently uses automatic trending categories. Select
              categories below and save to override.
            </div>
          ) : null}

          <section className="sa-sql-order-card">
            <h2 className="sa-sql-order-card__title">Home page order (highest activity first)</h2>
            {selectedCategoriesByActivity.length ? (
              <div className="sa-sql-order-flow">
                {selectedCategoriesByActivity.map((category, index) => (
                  <React.Fragment key={category.id}>
                    <div className="sa-sql-order-flow__item">
                      <span className="sa-sql-order-flow__num">{index + 1}</span>
                      <span className="sa-sql-order-flow__label">{category.name}</span>
                    </div>
                    {index < selectedCategoriesByActivity.length - 1 ? (
                      <FaArrowRight className="sa-sql-order-flow__arrow" aria-hidden />
                    ) : null}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <p className="sa-tc-empty-order">
                Automatic trending categories are shown on the home page (sorted by products, suppliers, and RFQs).
              </p>
            )}
          </section>

          <section className="sa-sql-card">
            <div className="sa-sql-card__head sa-tc-card__head">
              <h2 className="sa-sql-card__title">
                Marketplace categories ({visibleCategoryIds.length} of {maxCategories} selected) — showing{" "}
                {visibleCategoryOptions.length} of {filteredCategoryOptions.length}
                {categorySearchQuery.trim() ? " matching" : ""}, sorted by activity
              </h2>
              <div className="mca-search sa-tc-search">
                <FaSearch className="mca-search__icon" aria-hidden />
                <input
                  type="search"
                  className="mca-input mca-search__input"
                  placeholder="Search categories…"
                  value={categorySearchQuery}
                  onChange={(event) => setCategorySearchQuery(event.target.value)}
                  aria-label="Search marketplace categories"
                />
              </div>
            </div>

            {filteredCategoryOptions.length ? (
            <ul className="sa-sql-tile-list sa-tc-category-list">
              {visibleCategoryOptions.map((category, activityIndex) => {
                const isOn = visibleCategoryIds.includes(category.id);
                const selection = getSelection(category.id);
                const selectedSubIds = selection?.subcategory_ids ?? [];
                const imageSrc = category.image_path ? resolvePublicFileUrl(category.image_path) : null;

                return (
                  <li
                    key={category.id}
                    className={`sa-tc-category-list__item${isOn ? "" : " sa-tc-category-list__item--off"}`}
                  >
                    <div className="sa-tc-category-list__main">
                      <span className="sa-sql-tile-list__grip" aria-hidden>
                        <FaGripVertical />
                      </span>
                      <span className={`sa-sql-tile-list__order${isOn ? "" : " sa-sql-tile-list__order--off"}`}>
                        {activityIndex + 1}
                      </span>
                      <span className="sa-tc-category-list__thumb">
                        {imageSrc ? <img src={imageSrc} alt="" /> : <FaBox aria-hidden />}
                      </span>
                      <div className="sa-sql-tile-list__copy">
                        <span className="sa-sql-tile-list__title">{category.name}</span>
                        <CategoryStats item={category} />
                        <span className="sa-sql-tile-list__desc">
                          {isOn
                            ? `${selectedSubIds.length} of ${TRADING_CATEGORY_MAX_SUBCATEGORIES} sub-categories selected`
                            : "Hidden from home page"}
                        </span>
                      </div>
                      <div className="sa-sql-tile-list__controls">
                        <ToggleSwitch
                          checked={isOn}
                          disabled={
                            saving ||
                            (isOn && categorySelections.length <= 1) ||
                            (!isOn && atCategoryLimit)
                          }
                          onChange={(checked) => toggleCategory(category.id, checked)}
                          label={`${isOn ? "Hide" : "Show"} ${category.name} on home`}
                        />
                        {isOn ? (
                          <button
                            type="button"
                            className="sa-tc-expand-btn"
                            onClick={() => setSubcategoryModalCategoryId(category.id)}
                          >
                            <FaChevronDown aria-hidden />
                            <span>Sub-categories</span>
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            ) : (
              <p className="sa-tc-search-empty">
                No categories match &ldquo;{categorySearchQuery.trim()}&rdquo;. Try a different search term.
              </p>
            )}

            {hasMoreCategories ? (
              <div className="sa-tc-load-more">
                <button
                  type="button"
                  className="sa-tc-load-more__btn"
                  onClick={() =>
                    setVisibleCategoryCount((count) => count + TRADING_CATEGORIES_PAGE_SIZE)
                  }
                >
                  View More
                  <span className="sa-tc-load-more__count">
                    ({nextCategoryBatchSize} more{" "}
                    {nextCategoryBatchSize === 1 ? "category" : "categories"})
                  </span>
                  <FaChevronRight aria-hidden />
                </button>
              </div>
            ) : null}
          </section>

          <section className="sa-sql-info-grid">
            <div className="sa-sql-info-card">
              <h3>Activity Ranking</h3>
              <p>
                Categories are listed highest to lowest by products + suppliers + RFQs. Selected categories appear on
                the home page in the same activity order.
              </p>
            </div>
            <div className="sa-sql-info-card">
              <h3>Sub-categories</h3>
              <p>
                Click Sub-categories on a selected category to open the popup. Pick up to four sub-categories ranked by
                activity. If none are selected, the top trending sub-categories are used automatically.
              </p>
            </div>
            <div className="sa-sql-info-card">
              <h3>Grid Layout</h3>
              <p>
                Rows and categories-per-row are configured from{" "}
                <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>.
              </p>
            </div>
          </section>
        </>
      )}

      <SubcategoriesModal
        open={Boolean(subcategoryModalCategoryId && subcategoryModalCategory)}
        category={subcategoryModalCategory}
        saving={saving}
        selectedSubIds={subcategoryModalSelection?.subcategory_ids ?? []}
        subcategories={subcategoryModalCategoryId ? subcategoryCache[subcategoryModalCategoryId] || [] : []}
        loading={loadingSubcategoryId === subcategoryModalCategoryId}
        onClose={closeSubcategoriesModal}
        onToggleSubcategory={(subcategoryId, checked) =>
          subcategoryModalCategoryId &&
          toggleSubcategory(subcategoryModalCategoryId, subcategoryId, checked)
        }
        onMoveSubcategory={(subcategoryId, direction) =>
          subcategoryModalCategoryId &&
          moveSubcategory(subcategoryModalCategoryId, subcategoryId, direction)
        }
      />
    </div>
  );
}
